import { useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "@/pages/staff/StaffLayout";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FieldError } from "@/components/FormFeedback";
import { toast } from "sonner";
import { Search, UserCheck, UserPlus } from "lucide-react";
import { validateMobile, cleanMobile, validateDOB, calculateAge, validateAge, validateNotFutureDate } from "@/lib/validation";
import { cn } from "@/lib/utils";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";
import { ShieldAlert } from "lucide-react";

const EMPTY_PATIENT = {
  full_name: "", mobile: "", dob: "", age: "", gender: "", address: "",
  primary_diagnosis: "", diagnosis_date: "", known_conditions: "", past_medical_history: "",
  family_history: "", allergies: "", height: "", weight: "", blood_pressure: "",
  previous_medicines: "", previous_prescriptions_note: "", previous_clinic_name: "",
  previous_doctor_name: "", previous_treatment_location: "",
};

export default function NewAppointmentPage() {
  const navigate = useNavigate();
  const isReadOnly = useIsGracePeriod();
  const [stage, setStage] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [mobileTouched, setMobileTouched] = useState(false);
  const [matches, setMatches] = useState([]);
  const [patientForm, setPatientForm] = useState({ ...EMPTY_PATIENT });
  const [npTouched, setNpTouched] = useState({});
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [apptForm, setApptForm] = useState({
    appointment_date: new Date().toISOString().slice(0, 10),
    appointment_time: new Date().toTimeString().slice(0, 5),
    appointment_type: "NEW_VISIT",
    notes: "",
  });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const mobileError = validateMobile(mobile);

  const checkDuplicate = async (e) => {
    e.preventDefault();
    setMobileTouched(true);
    if (mobileError) return;
    setLoading(true);
    try {
      const { data } = await api.post("/patients/check-duplicate", { mobile: cleanMobile(mobile) });
      if (data.found) {
        setMatches(data.patients);
        setStage("duplicate_found");
      } else {
        setPatientForm({ ...EMPTY_PATIENT, mobile: cleanMobile(mobile) });
        setStage("new_patient_form");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const openExisting = (p) => {
    setSelectedPatient(p);
    setStage("appointment_form");
  };

  const continueAsNew = () => {
    setPatientForm({ ...EMPTY_PATIENT, mobile: cleanMobile(mobile) });
    setStage("new_patient_form");
  };

  const npFieldErrors = {
    full_name: !patientForm.full_name.trim() ? "Full name is required" : null,
    mobile: validateMobile(patientForm.mobile),
    dob: validateDOB(patientForm.dob),
    age: validateAge(patientForm.age),
    diagnosis_date: validateNotFutureDate(patientForm.diagnosis_date, "Diagnosis date"),
  };
  const npIsValid = Object.values(npFieldErrors).every((e) => !e);

  const submitNewPatient = async (e) => {
    e.preventDefault();
    setNpTouched({ full_name: true, mobile: true, dob: true, age: true, diagnosis_date: true });
    if (!npIsValid) return;
    setLoading(true);
    try {
      const { data } = await api.post("/patients", { ...patientForm, mobile: cleanMobile(patientForm.mobile), age: patientForm.age ? Number(patientForm.age) : null });
      setSelectedPatient(data);
      setStage("appointment_form");
      toast.success(`Patient registered: ${data.patient_code}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitAppointment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/appointments", { ...apptForm, patient_id: selectedPatient.id });
      for (const f of uploadFiles) {
        const fd = new FormData();
        fd.append("category", "previous_report");
        fd.append("appointment_id", data.id);
        fd.append("file", f);
        await api.post(`/reports/${selectedPatient.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Appointment ${data.appointment_code} created. Patient added to queue.`);
      navigate("/staff/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const setP = (k, v) => setPatientForm((f) => ({ ...f, [k]: v }));
  const setDob = (v) => {
    const age = calculateAge(v);
    setPatientForm((f) => ({ ...f, dob: v, age: age === "" ? f.age : String(age) }));
  };
  const markNpTouched = (k) => setNpTouched((t) => ({ ...t, [k]: true }));
  const npErrClass = (k) => cn(npTouched[k] && npFieldErrors[k] && "border-red-500 focus-visible:ring-red-500");

  return (
    <StaffLayout>
      <h1 className="text-2xl font-semibold text-slate-900" data-testid="new-appointment-heading">Create Appointment</h1>

      {isReadOnly ? (
        <div
          data-testid="new-appointment-readonly-block"
          className="mt-6 flex max-w-md items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            New appointments cannot be created while your clinic's subscription is in its grace period. Contact your
            Super Admin to renew and restore full access.
          </p>
        </div>
      ) : (
      <>
      {stage === "mobile" && (
        <form onSubmit={checkDuplicate} className="mt-6 max-w-md card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <Label>Mobile Number</Label>
          <Input
            data-testid="duplicate-check-mobile-input"
            required
            value={mobile}
            maxLength={10}
            className={cn(mobileTouched && mobileError && "border-red-500 focus-visible:ring-red-500")}
            onBlur={() => setMobileTouched(true)}
            onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
            placeholder="Enter patient's mobile number"
          />
          {mobileTouched && <FieldError testId="duplicate-check-mobile-error" message={mobileError} />}
          <Button data-testid="duplicate-check-submit-btn" type="submit" disabled={loading || !!mobileError} className="mt-4 w-full gap-2 bg-teal-700 hover:bg-teal-800">
            <Search className="h-4 w-4" /> {loading ? "Checking..." : "Check Patient"}
          </Button>
        </form>
      )}

      {stage === "duplicate_found" && (
        <div className="mt-6 max-w-md rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="duplicate-found-panel">
          <p className="text-sm font-medium text-amber-800">Existing patient(s) found for this mobile number:</p>
          <div className="mt-3 space-y-2">
            {matches.map((p) => (
              <div key={p.id} className="rounded-lg border border-amber-200 bg-white p-3">
                <p className="font-medium text-slate-900">{p.full_name}</p>
                <p className="text-xs font-mono text-slate-500">{p.patient_code}</p>
                <Button size="sm" data-testid={`open-existing-${p.patient_code}`} className="mt-2 gap-2 bg-teal-700 hover:bg-teal-800" onClick={() => openExisting(p)}>
                  <UserCheck className="h-4 w-4" /> Open Existing Profile
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" data-testid="continue-as-new-btn" className="mt-4 w-full gap-2" onClick={continueAsNew}>
            <UserPlus className="h-4 w-4" /> Continue as New Patient
          </Button>
        </div>
      )}

      {stage === "new_patient_form" && (
        <form onSubmit={submitNewPatient} className="mt-6 max-w-3xl card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <h2 className="font-semibold text-slate-900">Personal Details</h2>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label>Full Name</Label>
              <Input data-testid="np-full-name" required value={patientForm.full_name} onBlur={() => markNpTouched("full_name")} className={npErrClass("full_name")} onChange={(e) => setP("full_name", e.target.value)} />
              {npTouched.full_name && <FieldError testId="np-full-name-error" message={npFieldErrors.full_name} />}
            </div>
            <div>
              <Label>Mobile Number</Label>
              <Input data-testid="np-mobile" required maxLength={10} value={patientForm.mobile} onBlur={() => markNpTouched("mobile")} className={npErrClass("mobile")} onChange={(e) => setP("mobile", e.target.value.replace(/[^\d]/g, "").slice(0, 10))} />
              {npTouched.mobile && <FieldError testId="np-mobile-error" message={npFieldErrors.mobile} />}
            </div>
            <div>
              <Label>DOB</Label>
              <DatePicker testId="np-dob" value={patientForm.dob} onChange={setDob} toYear={new Date().getFullYear()} />
              {npFieldErrors.dob && <FieldError testId="np-dob-error" message={npFieldErrors.dob} />}
            </div>
            <div>
              <Label>Age {patientForm.dob && <span className="text-xs text-slate-400">(auto-calculated from DOB)</span>}</Label>
              <Input data-testid="np-age" type="number" min="0" max="130" value={patientForm.age} onBlur={() => markNpTouched("age")} className={npErrClass("age")} onChange={(e) => setP("age", e.target.value)} />
              {npTouched.age && <FieldError testId="np-age-error" message={npFieldErrors.age} />}
            </div>
            <div>
              <Label>Gender</Label>
              <Select value={patientForm.gender} onValueChange={(v) => setP("gender", v)}>
                <SelectTrigger data-testid="np-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Address</Label><Input data-testid="np-address" value={patientForm.address} onChange={(e) => setP("address", e.target.value)} /></div>
          </div>

          <h2 className="mt-6 font-semibold text-slate-900">Medical Details</h2>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><Label>Primary Diagnosis</Label><Input data-testid="np-diagnosis" value={patientForm.primary_diagnosis} onChange={(e) => setP("primary_diagnosis", e.target.value)} /></div>
            <div>
              <Label>Diagnosis Date</Label>
              <DatePicker testId="np-diagnosis-date" value={patientForm.diagnosis_date} onChange={(v) => setP("diagnosis_date", v)} toYear={new Date().getFullYear()} />
              {npFieldErrors.diagnosis_date && <FieldError testId="np-diagnosis-date-error" message={npFieldErrors.diagnosis_date} />}
            </div>
            <div><Label>Known Medical Conditions</Label><Textarea rows={2} value={patientForm.known_conditions} onChange={(e) => setP("known_conditions", e.target.value)} /></div>
            <div><Label>Past Medical History</Label><Textarea rows={2} value={patientForm.past_medical_history} onChange={(e) => setP("past_medical_history", e.target.value)} /></div>
            <div><Label>Family History</Label><Textarea rows={2} value={patientForm.family_history} onChange={(e) => setP("family_history", e.target.value)} /></div>
            <div><Label>Allergies</Label><Textarea rows={2} value={patientForm.allergies} onChange={(e) => setP("allergies", e.target.value)} /></div>
            <div><Label>Height (cm)</Label><Input value={patientForm.height} onChange={(e) => setP("height", e.target.value)} /></div>
            <div><Label>Weight (kg)</Label><Input value={patientForm.weight} onChange={(e) => setP("weight", e.target.value)} /></div>
            <div><Label>Blood Pressure (mm Hg)</Label><Input value={patientForm.blood_pressure} onChange={(e) => setP("blood_pressure", e.target.value)} /></div>
            <div><Label>Previous Medicines</Label><Input value={patientForm.previous_medicines} onChange={(e) => setP("previous_medicines", e.target.value)} /></div>
            <div><Label>Previous Clinic/Hospital Name</Label><Input value={patientForm.previous_clinic_name} onChange={(e) => setP("previous_clinic_name", e.target.value)} /></div>
            <div><Label>Previous Doctor Name (optional)</Label><Input value={patientForm.previous_doctor_name} onChange={(e) => setP("previous_doctor_name", e.target.value)} /></div>
            <div><Label>Previous Treatment Location</Label><Input value={patientForm.previous_treatment_location} onChange={(e) => setP("previous_treatment_location", e.target.value)} /></div>
          </div>

          <Button data-testid="submit-new-patient-btn" type="submit" disabled={loading || !npIsValid} className="mt-6 bg-teal-700 hover:bg-teal-800">
            {loading ? "Registering..." : "Register Patient & Continue"}
          </Button>
        </form>
      )}

      {stage === "appointment_form" && selectedPatient && (
        <form onSubmit={submitAppointment} className="mt-6 max-w-2xl card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="rounded-lg bg-teal-50 p-3 text-sm text-teal-800">
            Patient: <span className="font-medium">{selectedPatient.full_name}</span> ({selectedPatient.patient_code})
          </div>
          <h2 className="mt-4 font-semibold text-slate-900">Appointment Details</h2>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><Label>Appointment Date</Label><DatePicker testId="appt-date" required value={apptForm.appointment_date} onChange={(v) => setApptForm((f) => ({ ...f, appointment_date: v }))} /></div>
            <div><Label>Appointment Time</Label><Input data-testid="appt-time" type="time" required value={apptForm.appointment_time} onChange={(e) => setApptForm((f) => ({ ...f, appointment_time: e.target.value }))} /></div>
            <div>
              <Label>Appointment Type</Label>
              <Select value={apptForm.appointment_type} onValueChange={(v) => setApptForm((f) => ({ ...f, appointment_type: v }))}>
                <SelectTrigger data-testid="appt-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW_VISIT">New Visit</SelectItem>
                  <SelectItem value="FOLLOW_UP">Follow-up</SelectItem>
                  <SelectItem value="EMERGENCY">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="appt-notes" value={apptForm.notes} onChange={(e) => setApptForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Reason for visit" /></div>
            <div className="sm:col-span-2">
              <Label>Upload Latest Reports (optional)</Label>
              <Input data-testid="appt-upload-files" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUploadFiles(Array.from(e.target.files))} />
            </div>
          </div>
          <Button data-testid="submit-appointment-btn" type="submit" disabled={loading} className="mt-6 bg-teal-700 hover:bg-teal-800">
            {loading ? "Creating..." : "Create Appointment"}
          </Button>
        </form>
      )}
      </>
      )}
    </StaffLayout>
  );
}
